import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ContentLayout from "@cloudscape-design/components/content-layout";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Alert from "@cloudscape-design/components/alert";
import Spinner from "@cloudscape-design/components/spinner";
import Box from "@cloudscape-design/components/box";
import GatewayTargetTile from "./GatewayTargetTile";
import GatewayTargetModal from "./GatewayTargetModal";
import GatewayTargetDeleteModal from "./GatewayTargetDeleteModal";
import { useAuth } from "../contexts/AuthContext";
import { 
  listGatewayTargets, 
  createGatewayTarget, 
  updateGatewayTarget, 
  deleteGatewayTarget,
  syncGatewayTarget
} from "../services/gatewayApi";
import "./ScenariosView.css";

function GatewayTargetsView() {
  const { t } = useTranslation();
  const { isWriteUser } = useAuth();
  
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedTarget, setSelectedTarget] = useState(null);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await listGatewayTargets();
      setTargets(data || []);
    } catch (err) {
      setError(err.message || t("gateway-targets.errors.fetch_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const handleCreateTarget = async (data) => {
    try {
      await createGatewayTarget(data);
      setShowModal(false);
      await fetchTargets();
    } catch (err) {
      if (err.statusCode === 403) {
        setError(t("errors.api.forbidden"));
        setShowModal(false);
      }
      throw err;
    }
  };

  const handleEditTarget = async (data, targetId) => {
    try {
      await updateGatewayTarget(targetId, data);
      setShowModal(false);
      setSelectedTarget(null);
      await fetchTargets();
    } catch (err) {
      if (err.statusCode === 403) {
        setError(t("errors.api.forbidden"));
        setShowModal(false);
        setSelectedTarget(null);
      }
      throw err;
    }
  };

  const handleDeleteTarget = async () => {
    if (!selectedTarget) return;
    setDeleteLoading(true);
    
    try {
      await deleteGatewayTarget(selectedTarget.target_id);
      setShowDeleteModal(false);
      setSelectedTarget(null);
      await fetchTargets();
    } catch (err) {
      if (err.statusCode === 403) {
        setError(t("errors.api.forbidden"));
        setShowDeleteModal(false);
        setSelectedTarget(null);
        await fetchTargets();
      } else {
        setError(err.message || t("gateway-targets.errors.delete_failed"));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSyncTarget = async (target) => {
    try {
      await syncGatewayTarget(target.target_id);
      await fetchTargets();
    } catch (err) {
      if (err.statusCode === 403) {
        setError(t("errors.api.forbidden"));
      } else {
        setError(err.message || t("gateway-targets.errors.sync_failed"));
      }
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("gateway-targets.view.description")}
          actions={
            isWriteUser && (
              <Button 
                variant="primary" 
                iconName="add-plus" 
                onClick={() => { setModalMode("create"); setSelectedTarget(null); setShowModal(true); }}
              >
                {t("gateway-targets.view.add_button")}
              </Button>
            )
          }
        >
          {t("gateway-targets.view.title")}
        </Header>
      }
    >
      <Container>
        <SpaceBetween size="l">
          {error && (
            <Alert 
              type="error" 
              dismissible 
              onDismiss={() => setError(null)}
              action={<Button onClick={fetchTargets}>{t("errors.api.try_again")}</Button>}
            >
              {error}
            </Alert>
          )}

          {loading && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner size="large" />
              <Box variant="p" margin={{ top: "s" }}>{t("gateway-targets.view.loading")}</Box>
            </div>
          )}

          {!loading && targets.length === 0 && (
            <Box textAlign="center" padding={{ vertical: "xxl" }}>
              <SpaceBetween size="m">
                <Box variant="h2" color="text-body-secondary">{t("gateway-targets.view.empty_title")}</Box>
                <Box variant="p" color="text-body-secondary">{t("gateway-targets.view.empty_message")}</Box>
                {isWriteUser && (
                  <Button 
                    variant="primary" 
                    iconName="add-plus" 
                    onClick={() => { setModalMode("create"); setShowModal(true); }}
                  >
                    {t("gateway-targets.view.add_first")}
                  </Button>
                )}
              </SpaceBetween>
            </Box>
          )}

          {!loading && targets.length > 0 && (
            <div className="gateway-targets-grid">
              {targets.map((target) => (
                <GatewayTargetTile
                  key={target.target_id || target.name}
                  target={target}
                  isWriteUser={isWriteUser}
                  onEdit={(t) => { setModalMode("edit"); setSelectedTarget(t); setShowModal(true); }}
                  onDelete={(t) => { setSelectedTarget(t); setShowDeleteModal(true); }}
                  onSync={handleSyncTarget}
                />
              ))}
            </div>
          )}
        </SpaceBetween>
      </Container>

      <GatewayTargetModal
        key={selectedTarget?.target_id || 'create'}
        mode={modalMode}
        target={selectedTarget}
        onSubmit={modalMode === "create" ? handleCreateTarget : handleEditTarget}
        onCancel={() => { setShowModal(false); setSelectedTarget(null); }}
        visible={showModal}
      />

      <GatewayTargetDeleteModal
        target={selectedTarget}
        onConfirm={handleDeleteTarget}
        onCancel={() => { setShowDeleteModal(false); setSelectedTarget(null); }}
        visible={showDeleteModal}
        loading={deleteLoading}
      />
    </ContentLayout>
  );
}

export default GatewayTargetsView;
